import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import { toast } from "sonner";
import { IKO, formatEAT } from "@/lib/design";
import { confirm } from "@/lib/dialog";

export default function SettingsPage() {
  const [s, setS] = useState({ admin_allowlist: [], jsonld_seed_urls: [], sitemap_seed_urls: [], auto_approval_threshold: 0.0 });
  const [allowRaw, setAllowRaw] = useState("");
  const [seedRaw, setSeedRaw] = useState("");
  const [sitemapRaw, setSitemapRaw] = useState("");
  const [thresh, setThresh] = useState("0");
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [purging, setPurging] = useState(false);
  // W-B.R24.6 · backfill-poster-check admin trigger state.
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillDryRanAt, setBackfillDryRanAt] = useState(0);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const [r, runsRes, candRes] = await Promise.all([
      client.get("/admin/settings"),
      client.get("/admin/sources/jsonld/runs?limit=25"),
      client.get("/admin/purge/candidates"),
    ]);
    setS(r.data.settings);
    setAllowRaw((r.data.settings.admin_allowlist || []).join("\n"));
    setSeedRaw((r.data.settings.jsonld_seed_urls || []).join("\n"));
    setSitemapRaw((r.data.settings.sitemap_seed_urls || []).join("\n"));
    setThresh(String(r.data.settings.auto_approval_threshold || 0));
    setRuns(runsRes.data.runs || []);
    setCandidates(candRes.data.candidates || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      admin_allowlist: allowRaw.split("\n").map((s) => s.trim()).filter(Boolean),
      jsonld_seed_urls: seedRaw.split("\n").map((s) => s.trim()).filter(Boolean),
      sitemap_seed_urls: sitemapRaw.split("\n").map((s) => s.trim()).filter(Boolean),
      auto_approval_threshold: Number(thresh) || 0,
    };
    await client.put("/admin/settings", payload);
    toast.success("Settings saved");
    await load();
  };

  const runCrawl = async () => {
    setRunning(true);
    try {
      const r = await client.post("/admin/sources/jsonld/run");
      toast.success(`Crawl kicked — ${r.data.run_ids.length} run(s). See the log below.`);
      await load();
    } catch { toast.error("Crawl failed"); } finally { setRunning(false); }
  };

  const purgeHeuristic = async () => {
    if (!(await confirm({ title: `purge ${candidates.length} candidate row(s)?`, message: "deletes from events + pending + raw_inputs. cannot be undone.", tone: "danger", confirmLabel: "yes, purge" }))) return;
    setPurging(true);
    try {
      // Safety rail: the backend requires confirm_count == current heuristic count.
      const r = await client.post("/admin/purge", {
        use_heuristics: true,
        confirm_count: candidates.length,
        also_delete_raw_inputs: true,
      });
      toast.success(`Purged ${r.data.purged_events} events + ${r.data.purged_pending} pending`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.error === "confirm_count_mismatch") {
        toast.error(`Candidate list changed (was ${candidates.length}, now ${detail.expected}). Refreshed — try again.`);
      } else {
        toast.error("Purge failed");
      }
      await load();
    } finally { setPurging(false); }
  };

  // Founder ask #5: embed palette fixed to the locked kit values.
  const embedSnippet = useMemo(() => `<!-- watu wako embedded events grid — paste anywhere in your Netlify site -->
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap">
<style>
  .ww-grid {
    /* Design tokens — override on your host page to restyle */
    --ww-sherehe: ${IKO.sherehe.hex};
    --ww-polite:  ${IKO.polite.hex};
    --ww-michezo: ${IKO.michezo.hex};
    --ww-kazi:    ${IKO.kazi.hex};
    --ww-kili:    #FFD60A;
    --ww-fg:      #1A1A30;
    --ww-fg-muted:#5C5548;
    --ww-card-bg:#ffffff;
    --ww-card-line:#e6e6ec;
    font-family: "Poppins", Arial, sans-serif;
    color: var(--ww-fg);
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
  }
  .ww-card { display:block; border:1px solid var(--ww-card-line); border-radius:14px; overflow:hidden; text-decoration:none; color:inherit; background:var(--ww-card-bg); transition: transform 200ms ease }
  .ww-card:hover { transform: translateY(-2px) }
  .ww-card img { width:100%; aspect-ratio:16/9; object-fit:cover; display:block }
  .ww-card .ww-body { padding:16px }
  .ww-chip { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; letter-spacing:.02em; }
  .ww-chip::before { content:""; width:8px; height:8px; border-radius:50%; background:currentColor; display:inline-block }
  .ww-title { margin:8px 0 4px; font-size:18px; font-weight:700 }
  .ww-meta { margin:0; font-size:13px; color:var(--ww-fg-muted) }
  .ww-price { margin:8px 0 0; font-size:13px; font-weight:600 }
  .ww-ticket { margin-top:6px; display:inline-block; font-size:12px; font-weight:600; color:var(--ww-fg); border-bottom:2px solid var(--ww-kili) }
</style>
<div id="watuwako-events" class="ww-grid"></div>
<script>
(async () => {
  const root = document.getElementById("watuwako-events");
  const IKO_COLOR = {
    sherehe: getComputedStyle(root).getPropertyValue("--ww-sherehe").trim(),
    polite:  getComputedStyle(root).getPropertyValue("--ww-polite").trim(),
    michezo: getComputedStyle(root).getPropertyValue("--ww-michezo").trim(),
    kazi:    getComputedStyle(root).getPropertyValue("--ww-kazi").trim(),
  };
  try {
    const r = await fetch("${backend}/api/public/events?limit=24");
    const { events } = await r.json();
    if (!events.length) { root.innerHTML = "<p>No upcoming events yet.</p>"; return; }
    root.innerHTML = events.map(e => {
      const href = e.ticket_link || "#";
      const color = IKO_COLOR[e.iko] || "#1A1A30";
      const when = new Date(e.start_at).toLocaleString("en-GB",{timeZone:"Africa/Nairobi",day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"});
      return \`
        <a class="ww-card" href="\${href}" \${e.ticket_link ? 'target="_blank" rel="noreferrer"' : ""}>
          \${e.cover_image_url ? \`<img src="\${e.cover_image_url}" alt="" onerror="this.style.display='none'" />\` : ""}
          <div class="ww-body">
            <span class="ww-chip" style="color:\${color}">\${(e.iko||"").toUpperCase()}</span>
            <h3 class="ww-title">\${e.title}</h3>
            <p class="ww-meta">\${when} · \${e.venue_display||""}</p>
            <p class="ww-price">\${e.price||"TBA"}</p>
            \${e.ticket_link ? '<span class="ww-ticket">Get tickets →</span>' : ""}
          </div>
        </a>\`;
    }).join("");
  } catch (e) { root.innerHTML = "<p>Unable to load events.</p>"; }
})();
</script>`, [backend]);

  return (
    <div className="min-w-0 max-w-full overflow-x-hidden p-4 md:p-8">
      <p className="label-tiny">watu wako</p>
      <h1 className="font-display text-3xl font-bold">Settings & Sources</h1>

      {/* Row 1 — allow-list + threshold */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
          <p className="label-tiny mb-2">Admin allow-list (one Google email per line)</p>
          <textarea data-testid={SETTINGS.allowlistInput} rows={5} className="w-full rounded-md border p-2 font-mono text-xs" style={{ borderColor: "var(--line-cream)" }}
            value={allowRaw} onChange={(e) => setAllowRaw(e.target.value)} />
          <p className="mt-1 text-xs" style={{ color: "var(--text-dim-cream)" }}>Only these emails can reach /admin. Seeded from env at startup.</p>
        </div>

        <div className="rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
          <p className="label-tiny mb-2">Auto-approval confidence threshold</p>
          <input data-testid={SETTINGS.thresholdInput} type="number" step="0.05" min="0" max="1"
            className="w-32 rounded-md border p-2 text-sm" style={{ borderColor: "var(--line-cream)" }}
            value={thresh} onChange={(e) => setThresh(e.target.value)} />
          <p className="mt-1 text-xs" style={{ color: "var(--text-dim-cream)" }}>0 = every event needs human eyes (recommended for beta).</p>
        </div>
      </div>

      {/* Row 2 — seed URLs (event pages + sitemaps) */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
          <p className="label-tiny mb-2">Individual event pages (JSON-LD)</p>
          <p className="text-xs mb-2" style={{ color: "var(--text-dim-cream)" }}>Direct URLs to pages carrying <code>application/ld+json</code> of type Event.</p>
          <textarea data-testid={SETTINGS.seedUrlsInput} rows={6} className="w-full rounded-md border p-2 font-mono text-xs" style={{ borderColor: "var(--line-cream)" }}
            value={seedRaw} onChange={(e) => setSeedRaw(e.target.value)} placeholder="https://mookh.com/rnbthursdays/" />
        </div>

        <div className="rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
          <p className="label-tiny mb-2">Sitemaps (walk every &lt;loc&gt;)</p>
          <p className="text-xs mb-2" style={{ color: "var(--text-dim-cream)" }}>Sitemap URLs (including sitemap-index files). We walk every entry, fetch each page and extract JSON-LD.</p>
          <textarea rows={6} className="w-full rounded-md border p-2 font-mono text-xs" style={{ borderColor: "var(--line-cream)" }}
            value={sitemapRaw} onChange={(e) => setSitemapRaw(e.target.value)} placeholder="https://mookh.com/sitemap.xml" />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid={SETTINGS.saveButton} onClick={save} className="rounded-full bg-neutral-900 px-5 py-2 text-sm font-semibold text-white">Save settings</button>
        <button data-testid={SETTINGS.runCrawlButton} onClick={runCrawl} disabled={running}
          className="rounded-full border px-5 py-2 text-sm font-semibold transition-colors hover:bg-neutral-900 hover:text-white disabled:opacity-50"
          style={{ borderColor: "var(--line-cream)" }}>
          {running ? "Running crawler…" : "Run crawler now"}
        </button>
      </div>

      {/* Row 3 — Run log */}
      <div className="mt-10 rounded-lg border" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="label-tiny">Ingestion run log</p>
            <p className="text-xs" style={{ color: "var(--text-dim-cream)" }}>Newest first. Raw error text preserved verbatim.</p>
          </div>
          <button onClick={load} className="text-xs underline">Refresh</button>
        </div>
        <div className="overflow-x-auto" style={{ maxWidth: "calc(100vw - 64px)" }}>
          <table className="w-full text-xs">
            <thead style={{ background: "var(--well-cream)" }}>
              <tr>
                <th className="p-2 text-left">Started</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 text-right">HTTP</th>
                <th className="p-2 text-right">Found</th>
                <th className="p-2 text-right">New</th>
                <th className="p-2 text-right">Dedup</th>
                <th className="p-2 text-right">Failed</th>
                <th className="p-2 text-left">Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.run_id} className="border-t" style={{ borderColor: "var(--line-cream)" }}>
                  <td className="p-2 whitespace-nowrap">{formatEAT(r.started_at)}</td>
                  <td className="p-2"><span className="rounded px-2 py-0.5 text-[10px] font-semibold" style={{ background: r.source_type === "sitemap" ? "#FFD60A" : (r.source_type === "purge" ? "#FF6B9D" : "var(--well-cream)"), color: r.source_type === "purge" ? "#fff" : "#111" }}>{r.source_type}</span></td>
                  <td className="p-2 max-w-xs truncate"><span title={r.seed_url}>{r.seed_url}</span></td>
                  <td className="p-2 text-right">{r.http_status ?? "—"}</td>
                  <td className="p-2 text-right">{r.items_found}</td>
                  <td className="p-2 text-right font-semibold" style={{ color: r.items_new > 0 ? "#44DD88" : "inherit" }}>{r.items_new}</td>
                  <td className="p-2 text-right">{r.items_deduped}</td>
                  <td className="p-2 text-right" style={{ color: r.items_failed > 0 ? "#FF6B9D" : "inherit" }}>{r.items_failed}</td>
                  <td className="p-2 max-w-sm truncate" style={{ color: r.error ? "#c00" : "var(--text-dim-cream)" }}><code>{r.error || "—"}</code></td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={9} className="p-6 text-center" style={{ color: "var(--text-dim-cream)" }}>No runs logged yet. Hit "Run crawler now".</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row 4 — Purge test data */}
      <div className="mt-8 rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="label-tiny">Purge test / seed data</p>
            <p className="text-xs" style={{ color: "var(--text-dim-cream)" }}>Detected by heuristics (title contains regression/seed/test, description = repeated phrase, explicit is_seed flag).</p>
          </div>
          <button onClick={purgeHeuristic} disabled={purging || candidates.length === 0}
            className="rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
            style={{ background: candidates.length ? "#c00" : "#999" }}>
            {purging ? "Purging…" : `Purge ${candidates.length} record(s)`}
          </button>
        </div>
        {candidates.length > 0 ? (
          <ul className="mt-3 space-y-1 text-xs">
            {candidates.map((c) => (
              <li key={c.id} className="flex gap-2">
                <span className="rounded bg-black/5 px-1.5 py-0.5 text-[10px]">{c.kind}</span>
                <span className="truncate">{c.title || "(untitled)"}</span>
                {c.cover_image_url_kind === "base64_inline" && <span className="text-[10px] text-amber-700">base64 inline</span>}
                {c.flags.title_looks_seed && <span className="text-[10px] text-red-700">title match</span>}
                {c.flags.description_repeated_phrase && <span className="text-[10px] text-red-700">repeated phrase</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs" style={{ color: "var(--text-dim-cream)" }}>No test data detected — the public feed is clean.</p>
        )}
      </div>

      {/* W-B.R24.6 (Vania 11 Oct) · Poster-check backfill from the
          live admin UI so Vania never needs a prod shell. Dry-run
          first; "Run for real" only enables after a dry-run in the
          same session. */}
      <div className="mt-8 rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
        <p className="label-tiny">Poster-check backfill</p>
        <p className="text-xs" style={{ color: "var(--text-dim-cream)" }}>
          One-off migration (W-B.R17 grandfather). Marks already-approved + already-reviewed rows as
          "poster confirmed by legacy"; leaves fresh pending rows unchecked. Idempotent — safe to
          re-run.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button"
                  data-testid="backfill-dry-run-button"
                  onClick={async () => {
                    setBackfillBusy(true); setBackfillResult(null);
                    try {
                      const r = await client.post("/admin/backfill-poster-check", { dry_run: true });
                      setBackfillResult(r.data); setBackfillDryRanAt(Date.now());
                      toast.success(`dry run · db ${r.data.db_name}`);
                    } catch (e) {
                      toast.error(`dry run failed · ${e?.response?.data?.detail || e?.message || ""}`);
                    } finally { setBackfillBusy(false); }
                  }}
                  disabled={backfillBusy}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
                  style={{ borderColor: "#1A1A30", background: "#FFFFFF", color: "#1A1A30" }}>
            {backfillBusy ? "working…" : "Check poster status backfill (dry run)"}
          </button>
          <button type="button"
                  data-testid="backfill-run-button"
                  onClick={async () => {
                    if (!backfillDryRanAt) return;
                    const ok = await confirm({
                      title: "Run poster-check backfill for real?",
                      body: `This writes to db "${backfillResult?.db_name}". Idempotent, no secrets logged. Continue?`,
                      confirmLabel: "Run for real", cancelLabel: "Cancel",
                    });
                    if (!ok) return;
                    setBackfillBusy(true);
                    try {
                      const r = await client.post("/admin/backfill-poster-check", { dry_run: false });
                      setBackfillResult(r.data); setBackfillDryRanAt(0);
                      toast.success(`backfill done · db ${r.data.db_name}`);
                    } catch (e) {
                      toast.error(`backfill failed · ${e?.response?.data?.detail || e?.message || ""}`);
                    } finally { setBackfillBusy(false); }
                  }}
                  disabled={backfillBusy || !backfillDryRanAt}
                  title={!backfillDryRanAt ? "Run the dry run first" : ""}
                  className="rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-40"
                  style={{ background: backfillDryRanAt ? "#9A2222" : "#999" }}>
            Run for real
          </button>
        </div>
        {backfillResult && (
          <pre data-testid="backfill-result"
               className="mt-3 overflow-x-auto rounded-md border p-3 font-mono text-[11px] leading-5"
               style={{ borderColor: "var(--line-cream)", background: "var(--well-cream)", color: "#1A1A30" }}>
{`db: ${backfillResult.db_name}
dry_run: ${backfillResult.dry_run}
${backfillResult.line}
live: ${backfillResult.live}
reviewed_pending: ${backfillResult.reviewed_pending}
fresh_pending: ${backfillResult.fresh_pending}
actor: ${backfillResult.actor}
ran_at: ${backfillResult.ran_at}`}
          </pre>
        )}
      </div>

      {/* Row 5 — Embed snippet */}
      <div className="mt-8 rounded-lg border p-5" style={{ borderColor: "var(--line-cream)", background: "var(--card-cream)" }}>
        <p className="label-tiny mb-2">Public embed snippet</p>
        <p className="text-xs mb-2" style={{ color: "var(--text-dim-cream)" }}>Iko colours locked to the kit (pink / purple / blue / green). Font: Poppins. Includes ticket-link rendering.</p>
        <textarea data-testid={SETTINGS.embedSnippet} rows={14} readOnly className="w-full rounded-md border p-3 font-mono text-[10.5px]" style={{ borderColor: "var(--line-cream)", background: "var(--well-cream)" }} value={embedSnippet} />
        <p className="mt-1 text-xs" style={{ color: "var(--text-dim-cream)" }}>Static snapshot: <code>{backend}/api/public/events.json</code></p>
      </div>
    </div>
  );
}
